import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  DialogService,
  EmptyComponent,
  InputComponent,
  SearchComponent,
  SkeletonComponent,
  TableComponent,
  type ActionEvent,
  type SortChangeEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import { finalize } from 'rxjs';
import { AdminTenantsService } from '../../../../data-access/src/lib/admin-users/admin-tenants.service';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import type { AdminTenantSummary } from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  VaultAppToastService,
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  openVaultConfirmDialog,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  bootstrapVaultAdminModuleAccess,
  createAdminModuleAccessSignal,
} from '../admin-users/vault-admin-module-access.util';
import {
  buildTenantCreatedToast,
  buildTenantDeletedToast,
  buildTenantUpdatedToast,
  buildTenantActivatedToast,
  buildTenantDeactivatedToast,
  buildTenantActivateConfirmDialog,
  buildTenantDeactivateConfirmDialog,
  buildTenantDeleteConfirmDialog,
} from '../vault-module-feedback.util';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  description: string;
  statusLabel: string;
  is_active: boolean;
}

@Component({
  selector: 'app-tenants',
  templateUrl: './tenants.component.html',
  styleUrls: ['./tenants.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    ButtonComponent,
    InputComponent,
    CheckboxComponent,
    SearchComponent,
    TableComponent,
    EmptyComponent,
    SkeletonComponent,
    BadgeComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    VaultPageAlertComponent,
  ],
})
export class TenantsComponent implements OnInit {
  private readonly tenantsApi = inject(AdminTenantsService);
  private readonly dashboard = inject(DashboardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly vaultAppToast = inject(VaultAppToastService);
  private readonly dialogService = inject(DialogService);

  readonly canManage = createAdminModuleAccessSignal('superadmin_only');
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly tenants = signal<AdminTenantSummary[]>([]);
  readonly searchTerm = signal('');
  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];
  readonly tableSortColumn = signal<string | null>('name');
  readonly tableSortDirection = signal<'asc' | 'desc' | null>('asc');

  readonly formOpen = signal(false);
  readonly formMode = signal<'create' | 'edit'>('create');
  readonly editingTenant = signal<AdminTenantSummary | null>(null);
  readonly formName = signal('');
  readonly formSlug = signal('');
  readonly formDescription = signal('');
  readonly formActive = signal(true);

  readonly filteredTenants = computed(() => {
    const q = this.searchTerm().trim().toLowerCase();
    if (!q) return this.tenants();
    return this.tenants().filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q),
    );
  });

  readonly tableData = computed<TenantRow[]>(() =>
    this.filteredTenants().map((t) => ({
      id: t.id,
      name: t.name,
      slug: t.slug,
      description: t.description?.trim() || '—',
      statusLabel: t.is_active !== false ? 'Activa' : 'Inactiva',
      is_active: t.is_active !== false,
    })),
  );

  readonly tableConfig = computed<TableConfig<TenantRow>>(() => ({
    columns: [
      { key: 'name', label: 'Nombre', align: 'left', sortable: true },
      { key: 'slug', label: 'Código', align: 'left', sortable: true },
      { key: 'description', label: 'Descripción', align: 'left' },
      { key: 'statusLabel', label: 'Estado', align: 'center', width: '110px', cellTemplate: true },
    ],
    actions: [
      { label: 'Editar', icon: 'edit', action: 'edit' },
      {
        label: 'Desactivar',
        icon: 'LockOpen',
        action: 'toggle-active',
        color: 'warn',
        visible: (row) => row.is_active,
      },
      {
        label: 'Activar',
        icon: 'Lock',
        action: 'toggle-active',
        visible: (row) => !row.is_active,
      },
      { label: 'Eliminar', icon: 'delete', action: 'delete', color: 'warn' },
    ],
    showPagination: true,
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
    paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
    initialSort: { column: 'name', direction: 'asc' },
    ariaLabel: 'Listado de empresas',
    actionsLabel: 'Acciones',
    rowClickable: true,
    defaultAction: 'edit',
  }));

  readonly nameFieldTooltip =
    'Nombre comercial o razón social visible para los usuarios de la empresa.';
  readonly slugFieldTooltip =
    'Código corto y único de la empresa en el sistema (solo letras minúsculas, números y guiones). Se genera automáticamente desde el nombre; evite cambiarlo después de crear la empresa.';
  readonly descriptionFieldTooltip = 'Texto opcional para identificar la empresa en listados administrativos.';
  readonly activeFieldTooltip =
    'Empresa activa: los usuarios asignados pueden operar. Si está inactiva, el acceso queda restringido.';

  ngOnInit(): void {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
    if (this.canManage()) {
      this.reload();
    } else {
      this.loading.set(false);
    }
    bootstrapVaultAdminModuleAccess(
      this.canManage,
      'superadmin_only',
      this.dashboard,
      this.cdr,
      () => this.reload(),
    );
  }

  reload(opts?: { silent?: boolean }): void {
    if (!opts?.silent) this.loading.set(true);
    this.error.set(null);
    this.tenantsApi
      .list()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (list) => this.tenants.set(list),
        error: (err: unknown) =>
          this.setError(err, 'No se pudieron cargar las empresas.'),
      });
  }

  onTableSearchChange(value: string): void {
    this.searchTerm.set(value ?? '');
  }

  onTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) this.tablePageSize.set(ev.pageSize);
  }

  onTableSort(ev: SortChangeEvent<TenantRow>): void {
    this.tableSortColumn.set(ev.column != null ? String(ev.column) : null);
    this.tableSortDirection.set(ev.direction);
  }

  onTableAction(ev: ActionEvent<TenantRow>): void {
    const tenant = this.tenants().find((t) => t.id === ev.data.id);
    if (!tenant) return;
    if (ev.action === 'edit') this.openEdit(tenant);
    if (ev.action === 'toggle-active') this.openToggleConfirm(tenant);
    if (ev.action === 'delete') this.openDelete(tenant);
  }

  /** Actualiza `is_active` en memoria para que el candado cambie al instante (LockOpen ↔ Lock). */
  private patchTenantActiveState(tenantId: string, isActive: boolean): void {
    this.tenants.update((list) =>
      list.map((t) => (t.id === tenantId ? { ...t, is_active: isActive } : t)),
    );
    this.cdr.markForCheck();
  }

  openToggleConfirm(tenant: AdminTenantSummary): void {
    const config = tenant.is_active !== false
      ? buildTenantDeactivateConfirmDialog(tenant)
      : buildTenantActivateConfirmDialog(tenant);
    openVaultConfirmDialog(this.dialogService, config).subscribe((confirmed) => {
      if (confirmed) this.executeToggleActive(tenant);
    });
  }

  private executeToggleActive(tenant: AdminTenantSummary): void {
    const nextActive = tenant.is_active === false;
    this.saving.set(true);
    this.error.set(null);
    this.tenantsApi
      .update(tenant.id, { is_active: nextActive })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.patchTenantActiveState(tenant.id, nextActive);
          this.vaultAppToast.show(
            nextActive
              ? buildTenantActivatedToast(tenant.name)
              : buildTenantDeactivatedToast(tenant.name),
          );
          this.reload({ silent: true });
        },
        error: (err: unknown) => this.setError(err, 'No se pudo cambiar el estado de la empresa.'),
      });
  }

  openCreate(): void {
    this.formMode.set('create');
    this.editingTenant.set(null);
    this.formName.set('');
    this.formSlug.set('');
    this.formDescription.set('');
    this.formActive.set(true);
    this.formOpen.set(true);
  }

  openEdit(tenant: AdminTenantSummary): void {
    this.formMode.set('edit');
    this.editingTenant.set(tenant);
    this.formName.set(tenant.name);
    this.formSlug.set(tenant.slug);
    this.formDescription.set(tenant.description ?? '');
    this.formActive.set(tenant.is_active !== false);
    this.formOpen.set(true);
  }

  closeForm(): void {
    this.formOpen.set(false);
  }

  saveTenant(): void {
    const name = this.formName().trim();
    const slug = this.formSlug().trim();
    if (!name || !slug) {
      this.error.set('Nombre y código de empresa son obligatorios.');
      return;
    }
    const payload = {
      name,
      slug,
      description: this.formDescription().trim() || undefined,
      is_active: this.formActive(),
    };
    this.saving.set(true);
    this.error.set(null);
    if (this.formMode() === 'create') {
      this.tenantsApi
        .create(payload)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this.vaultAppToast.show(buildTenantCreatedToast(name));
            this.closeForm();
            this.reload();
          },
          error: (err: unknown) => this.setError(err, 'No se pudo crear la empresa.'),
        });
    } else {
      const ed = this.editingTenant();
      if (!ed) {
        this.saving.set(false);
        return;
      }
      this.tenantsApi
        .update(ed.id, payload)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this.vaultAppToast.show(buildTenantUpdatedToast(name));
            this.closeForm();
            this.reload({ silent: true });
          },
          error: (err: unknown) => this.setError(err, 'No se pudo actualizar la empresa.'),
        });
    }
  }

  openDelete(tenant: AdminTenantSummary): void {
    openVaultConfirmDialog(
      this.dialogService,
      buildTenantDeleteConfirmDialog(tenant),
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.confirmDelete(tenant);
      }
    });
  }

  private confirmDelete(tenant: AdminTenantSummary): void {
    this.saving.set(true);
    this.tenantsApi
      .delete(tenant.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.vaultAppToast.show(buildTenantDeletedToast(tenant.name));
          this.reload();
        },
        error: (err: unknown) => this.setError(err, 'No se pudo eliminar la empresa.'),
      });
  }

  onNameChange(v: string): void {
    this.formName.set(v);
    if (this.formMode() === 'create' && !this.formSlug().trim()) {
      this.formSlug.set(
        v
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, ''),
      );
    }
  }

  private setError(err: unknown, fallback: string): void {
    this.error.set(
      toModuleAccessErrorMessage(err, {
        moduleLabel: 'Gestión de Empresas',
        fallback,
      }),
    );
  }
}
